/**
 * DeviceUpdater Page Component
 *
 * Route handler for /updater that opens the Device Updater popup.
 * This allows direct navigation to the updater via URL while maintaining
 * the consistent popup UI pattern.
 *
 * Supports query param for initial step (dev mode only):
 *   /updater?step=device-connection
 */

import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { closeInnerPopup, openInnerPopup } from '../../../data/providers/popupsProvider';
import { WizardContainer } from './components/WizardContainer';
import { WizardStep } from './types/wizard-types';

/**
 * Parse and validate step from query params
 */
const getInitialStepFromUrl = (): WizardStep | undefined => {
  const params = new URLSearchParams(window.location.search);
  const step = params.get('step');

  if (step && Object.values(WizardStep).includes(step as WizardStep)) {
    return step as WizardStep;
  }

  return undefined;
};

/**
 * DeviceUpdater route component
 *
 * Opens the WizardContainer popup when the /updater route is accessed,
 * then redirects back to the dashboard.
 */
const DeviceUpdaterComponent: React.FC<RouteComponentProps> = ({ history }) => {
  React.useEffect(() => {
    // Read initial step from URL before redirect
    const initialStep = getInitialStepFromUrl();

    // Open the popup
    openInnerPopup(
      <WizardContainer onClose={closeInnerPopup(history)} initialStep={initialStep} />,
      history,
      'updater'
    );

    // Redirect to teams page (the popup will stay open due to hash)
    // Note: Must redirect to /teams, not /, because / redirects authenticated users to /teams
    history.replace('/teams#updater');
  }, [history]);

  // Return null as this component just triggers the popup
  return null;
};

export const DeviceUpdater = withRouter(DeviceUpdaterComponent);

export default DeviceUpdater;
